import { type FormEvent, useEffect, useId, useState } from 'react';
import { createPortal } from 'react-dom';
import { ApiError } from '../api/chat';
import { fetchOrganisationMembers, inviteMember } from '../api/organisation';
import type { InviteResponse, Organisation, OrganisationMember } from '../types/api';
import styles from './InviteDialog.module.css';

/**
 * What the admin passes on. No email leaves the backend on an invite, deliberately: the
 * platform's own invitation flow routes through two of its screens and cannot be pointed
 * back here. So the person who knows the colleague tells them, in whatever channel they
 * already share, and this is the text to tell them with.
 *
 * It describes the sign-in without promising a code or a password, because which one the
 * colleague meets depends on whether a Microsoft account already exists for their address
 * (measured 2026-08-31: it usually does at a utility), and a welcome message that promises
 * the wrong one reads like phishing.
 */
export function signInInstructions(name: string, email: string): string {
  const appUrl = `${window.location.origin}${import.meta.env.BASE_URL}`;
  return [
    `Hi ${name},`,
    '',
    `You now have access to DHI Docs AI, the documentation assistant for DHI software products.`,
    '',
    `1. Open ${appUrl}`,
    `2. Enter your work email address: ${email}`,
    '3. Sign in with Microsoft. If you already use a Microsoft account with this address you will be asked for its password; otherwise a one-time code is emailed to you.',
    '',
    'Please keep project and client specifics out of your questions.',
  ].join('\n');
}

/**
 * The domain part of an address, lowercased, or '' if there is no `@` at all. Mirrors
 * `email_domain()` in the backend's `core/organisation.py`, deliberately including the
 * empty answer for a malformed value, so two malformed addresses never match each other.
 */
export function emailDomain(email: string): string {
  const at = email.lastIndexOf('@');
  return at === -1
    ? ''
    : email
        .slice(at + 1)
        .trim()
        .toLowerCase();
}

/**
 * Whether what has been typed is a whole address yet. The domain rule is only worth
 * saying about a finished address: judged on every keystroke, `name@utility` is outside
 * the domain right up to the last character of it, and the refusal would appear while
 * somebody is still typing the thing that satisfies it.
 */
export function looksLikeAddress(email: string): boolean {
  const value = email.trim();
  const [local, domain, ...rest] = value.split('@');
  return rest.length === 0 && !!local && !!domain && domain.includes('.');
}

/**
 * Whether this admin may invite this address: same email domain, nothing else. The
 * backend enforces the same rule on `POST /organisation/members` and is the authority;
 * this is here so the refusal is immediate and worded before a round trip, not instead
 * of it.
 */
export function invitable(inviterEmail: string, invitee: string): boolean {
  const domain = emailDomain(inviterEmail);
  return domain !== '' && emailDomain(invitee.trim()) === domain;
}

/**
 * The member holding this address, or null. The roster does not always carry an email
 * (an identity the site list did not include shows as an id alone), and a member without
 * one can never match, which is why the backend's `already_member` answer stays the
 * backstop rather than this check replacing it.
 */
export function findMember(
  members: OrganisationMember[] | null,
  email: string,
): OrganisationMember | null {
  const target = email.trim().toLowerCase();
  if (!target) return null;
  return members?.find((m) => m.email?.toLowerCase() === target) ?? null;
}

/**
 * Whether a member matches what was typed in the search box. Matched against the label
 * the row actually shows, so a hit is always visible in the result rather than being
 * explained by a field nobody can see.
 */
export function matchesSearch(member: OrganisationMember, query: string): boolean {
  const q = query.trim().toLowerCase();
  return q === '' || memberLabel(member).toLowerCase().includes(q);
}

/**
 * Emails only: a name is whatever the inviter typed and the platform stored, and the
 * address is what identifies the person. The id is the last resort for an identity the
 * site roster did not include.
 */
export function memberLabel(member: OrganisationMember): string {
  return member.email || member.user_id;
}

/** The text shown inside the invite flow, since an invitee was never in any onboarding. */
const DISCLAIMER =
  'Invite people from your own organisation only. Questions and answers are stored to ' +
  'improve the assistant, so ask your colleagues to keep project and client specifics out ' +
  'of what they type. DHI Docs AI is in beta and its answers can be wrong; the ' +
  'documentation remains the reference.';

function messageFor(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'invite_cap_reached' || err.code === 'not_admin') return err.message;
    if (err.code === 'platform_unavailable')
      return 'MIKE Cloud did not respond. Please try again in a moment.';
    if (err.code === 'invites_unavailable') return err.message;
    return err.message;
  }
  return 'Something went wrong. Please try again.';
}

export default function InviteDialog({
  open,
  onClose,
  organisation,
  inviterEmail,
}: {
  open: boolean;
  onClose: () => void;
  organisation: Organisation | null;
  /** The signed-in admin's own address: the only domain they may invite into. */
  inviterEmail: string;
}) {
  const [members, setMembers] = useState<OrganisationMember[] | null>(null);
  const [membersError, setMembersError] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ response: InviteResponse; name: string } | null>(null);
  const [copied, setCopied] = useState(false);
  // The member list is its own view rather than a strip under the form, because it grows
  // with the organisation and a list that scrolls inside a form reads as clutter.
  const [view, setView] = useState<'invite' | 'members'>('invite');
  const [search, setSearch] = useState('');
  const titleId = useId();
  const nameId = useId();
  const emailId = useId();
  const searchId = useId();

  useEffect(() => {
    if (!open) return;
    setMembers(null);
    setMembersError(false);
    setResult(null);
    setError(null);
    setCopied(false);
    setView('invite');
    setSearch('');
    let cancelled = false;
    fetchOrganisationMembers()
      .then((r) => {
        if (!cancelled) setMembers(r.members);
      })
      .catch(() => {
        if (!cancelled) setMembersError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    if (existing) {
      setError(`${memberLabel(existing)} already has access.`);
      return;
    }
    if (!invitable(inviterEmail, email)) {
      setError(domainRule);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const response = await inviteMember(email.trim(), name.trim());
      setResult({ response, name: name.trim() });
      setName('');
      setEmail('');
      // The list is read fresh rather than patched, so it shows what the platform holds.
      fetchOrganisationMembers()
        .then((r) => setMembers(r.members))
        .catch(() => {});
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  }

  async function copyInstructions() {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(signInInstructions(result.name, result.response.email));
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  const memberCount = members === null ? '' : ` (${members.length})`;
  const shown = (members ?? []).filter((m) => matchesSearch(m, search));

  // Both checked as the address is typed, so an admin learns where the address is rather
  // than from the answer to a request that was never going to go through. Neither is
  // said in advance: the rules are only worth stating about an address that breaks one.
  const existing = findMember(members, email);

  const domain = emailDomain(inviterEmail);
  const domainRule = domain
    ? `You can only invite colleagues with an @${domain} address. Contact DHI to give access to someone outside your organisation.`
    : 'Your account cannot invite colleagues. Contact DHI.';
  const outsider = looksLikeAddress(email) && !invitable(inviterEmail, email);

  return createPortal(
    // biome-ignore lint/a11y/noStaticElementInteractions: backdrop dismiss is a mouse convenience; Escape is the keyboard path
    // biome-ignore lint/a11y/useKeyWithClickEvents: backdrop dismiss is a mouse convenience; Escape is the keyboard path
    <div
      className={styles.overlay}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={styles.card} role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <h2 id={titleId} className={styles.title}>
          {view === 'members' ? 'Who has access' : 'Invite colleagues'}
        </h2>
        <p className={styles.orgName}>{organisation?.name ?? 'Your organisation'}</p>

        {view === 'members' ? (
          <>
            {members === null && !membersError && <p className={styles.muted}>Loading...</p>}
            {membersError && <p className={styles.muted}>The list could not be loaded.</p>}
            {members !== null && members.length === 0 && (
              <p className={styles.muted}>Nobody yet.</p>
            )}
            {members !== null && members.length > 0 && (
              <>
                <input
                  id={searchId}
                  className={`${styles.input} ${styles.search}`}
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by email"
                  aria-label="Search members by email"
                  autoComplete="off"
                />
                {shown.length === 0 ? (
                  <p className={styles.muted}>No one matches "{search.trim()}".</p>
                ) : (
                  <ul className={styles.members}>
                    {shown.map((m) => (
                      <li key={m.user_id} className={styles.member}>
                        <span className={styles.memberWho}>{memberLabel(m)}</span>
                        <span className={styles.memberRole}>
                          {m.role === 'Owner' ? 'Administrator' : m.role}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
            <div className={styles.actions}>
              <button
                type="button"
                className={styles.btnSecondary}
                onClick={() => setView('invite')}
              >
                Back
              </button>
              <button type="button" className={styles.btnPrimary} onClick={onClose}>
                Close
              </button>
            </div>
          </>
        ) : result ? (
          <div className={styles.result} role="status">
            <p className={styles.resultLead}>
              {result.response.status === 'added'
                ? `${result.response.email} now has access.`
                : `${result.response.email} already had access.`}
            </p>
            <p className={styles.muted}>
              No email has been sent. Pass these instructions on to {result.name}:
            </p>
            <pre className={styles.instructions}>
              {signInInstructions(result.name, result.response.email)}
            </pre>
            <div className={styles.actions}>
              <button type="button" className={styles.btnSecondary} onClick={copyInstructions}>
                {copied ? 'Copied' : 'Copy instructions'}
              </button>
              <button type="button" className={styles.btnPrimary} onClick={() => setResult(null)}>
                Invite another
              </button>
            </div>
          </div>
        ) : (
          <form className={styles.form} onSubmit={submit} noValidate>
            <label className={styles.label} htmlFor={nameId}>
              Name
              <input
                id={nameId}
                className={styles.input}
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="off"
                required
              />
            </label>
            <label className={styles.label} htmlFor={emailId}>
              Work email
              <input
                id={emailId}
                className={styles.input}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="off"
                required
              />
            </label>
            {existing ? (
              <p className={`${styles.hint} ${styles.hintBlocked}`}>
                {memberLabel(existing)} already has access
                {existing.role === 'Owner' ? ', as an administrator.' : '.'}
              </p>
            ) : (
              outsider && <p className={`${styles.hint} ${styles.hintBlocked}`}>{domainRule}</p>
            )}
            <p className={styles.disclaimer}>{DISCLAIMER}</p>
            {error && (
              <p className={styles.error} role="alert">
                {error}
              </p>
            )}
            <div className={styles.actions}>
              <button type="button" className={styles.btnSecondary} onClick={onClose}>
                Close
              </button>
              <button
                type="submit"
                className={styles.btnPrimary}
                disabled={busy || !name.trim() || !email.trim() || existing !== null || outsider}
              >
                {busy ? 'Inviting...' : 'Give access'}
              </button>
            </div>
          </form>
        )}

        {view === 'invite' && (
          <button type="button" className={styles.linkBtn} onClick={() => setView('members')}>
            Who has access{memberCount}
          </button>
        )}
      </div>
    </div>,
    document.body,
  );
}
